import { Dossier, IntermediateReport, MediaPlan, MediaPlanDiscipline, MediaPlanFlightList, QueryParams } from '@abovo/shared-models';
import { SnackbarNotificationService } from '@abovo/shared-services';
import { HttpParamsHelper } from '@abovo/shared-utils';
import {
   DossierService,
   IntermediateCampaignReportService,
   MediaPlanDisciplineService,
   MediaPlanFlightService,
   MediaPlanService,
} from '@abovo/webvis-data-access';
import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

export type StaticReportData = {
   report: IntermediateReport;
   campaign: MediaPlan;
   flights: MediaPlanFlightList[];
   disciplines: MediaPlanDiscipline[];
   dossiers: Dossier[];
};

const mediaPlanParams = (campaignId: number) =>
   new HttpParamsHelper<MediaPlan>({ campaign_id: campaignId }).include({
      client: true,
      media_objectives: true,
      descriptions: { type: true, description: true },
      type: true,
   }).params as QueryParams & { campaign_id: number };

export const staticReportResolver: ResolveFn<StaticReportData> = async (route, state) => {
   const reportService = inject(IntermediateCampaignReportService);
   const campaignService = inject(MediaPlanService);
   const campaignFlightService = inject(MediaPlanFlightService);
   const disciplinesService = inject(MediaPlanDisciplineService);
   const dossierService = inject(DossierService);
   const router = inject(Router);
   const snackbar = inject(SnackbarNotificationService);
   const translate = inject(TranslateService);
   const reportId = route.params['reportId'];

   try {
      const report = await firstValueFrom(reportService.getById(reportId));
      const proposal = report.campaign_proposal;
      if (!proposal?.campaign_id) {
         throw new Error('The report has no mediaplan proposal');
      }
      const [campaign, flights, disciplines, dossiers] = await Promise.all([
         firstValueFrom(campaignService.single(mediaPlanParams(proposal.campaign_id)).pipe(map(({ result }) => result))),
         firstValueFrom(
            campaignFlightService.getCampaignFlightsList({
               campaign_ids: `${proposal.campaign_id}`,
               campaign_proposal_ids: `${proposal.id}`,
            }),
         ).then((response) => response.result),
         firstValueFrom(disciplinesService.get({ campaign_proposal_ids: `${proposal.id}` })),
         firstValueFrom(
            dossierService
               .get('reports', [reportId], { campaign_media_distributions: { category: true }, dossier_type: true, intermediate_report: true, creator: true })
               .pipe(map(({ result }) => result)),
         ),
      ]);

      return {
         report,
         campaign,
         flights,
         disciplines,
         dossiers,
      };
   } catch (error: unknown) {
      if (error instanceof HttpErrorResponse) {
         void router.navigate(['/', 'dashboard']);
         snackbar.showMessage({
            message: translate.instant('general.page_not_found', { subject: translate.instant('intermediate_report.report'), id: reportId }),
            extraClasses: ['mat-snack-bar-container--fail'],
            duration: 3000,
         });
         throw error;
      }

      return new RedirectCommand(router.parseUrl('/dashboard'));
   }
};
